/**
 * The Search Console band's per-brand state (UI-SPEC §2.1 `gsc`: a brand fact, not
 * reset on navigation) and the calls that drive it.
 *
 * Module scope, keyed by brandId, for the page session: it survives the composer
 * unmounting on « Vérifier » (so « Retour » lands on the same checked rows), the popup
 * closing, and the four axis pages that each host the popup. A full page load (the
 * OAuth round trip) starts empty. A call that finishes after its band unmounted still
 * lands here, so an import is never paid for twice (lot H step 13 cost rule).
 *
 * @see docs/plans/prompt-studio-build/H-search-console.md step 13
 */
import { useSyncExternalStore } from 'react';
import type { GscCallbackResult } from '@traaker/shared';
import { GEN_COOLDOWN_CODE, GSC_PROPOSE_MAX } from '@traaker/shared';
import { searchConsoleApi } from '../../services/search-console';
import { brandQueriesApi } from '../../services/brand-queries';
import type { GscAction, GscBrandState } from './searchConsoleModel';
import { INITIAL_GSC, currentImport, firstSeeds, gscReducer, pendingSeeds } from './searchConsoleModel';

const states = new Map<string, GscBrandState>();
const listeners = new Set<() => void>();
/** The `?gsc=` result the host page read, handed to the next band that opens. */
let pendingReturn: GscCallbackResult | null = null;

export function getGsc(brandId: string): GscBrandState {
  return states.get(brandId) ?? INITIAL_GSC;
}

export function dispatchGsc(brandId: string, a: GscAction): void {
  const prev = getGsc(brandId);
  const next = gscReducer(prev, a);
  if (next === prev) return;
  states.set(brandId, next);
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function useGsc(brandId: string): GscBrandState {
  const get = () => getGsc(brandId);
  return useSyncExternalStore(subscribe, get, get);
}

/** The host page read `?gsc=<result>` (and stripped it): the band shows it once and reloads. */
export function setPendingGscReturn(r: GscCallbackResult): void {
  pendingReturn = r;
}

/** Tests only. */
export function resetGscStore(): void {
  states.clear();
  pendingReturn = null;
}

/** The band opened: take the pending return, reload the status, import if nothing is cached. */
export async function openBand(brandId: string): Promise<void> {
  const r = pendingReturn;
  pendingReturn = null;
  dispatchGsc(brandId, r ? { type: 'returned', result: r } : { type: 'opened' });
  await loadStatus(brandId);
}

async function loadStatus(brandId: string): Promise<void> {
  if (getGsc(brandId).statusLoading) return;
  dispatchGsc(brandId, { type: 'statusStart' });
  try {
    dispatchGsc(brandId, { type: 'statusOk', status: await searchConsoleApi.status(brandId) });
  } catch {
    dispatchGsc(brandId, { type: 'statusFailed' });
    return;
  }
  // Auto-import ONLY with no cached result for this property, or right after `?gsc=connected` (cost rule).
  const s = getGsc(brandId);
  if (s.status?.state === 'connected' && !s.importing && (s.reimport || !currentImport(s))) await runImport(brandId);
}

async function runImport(brandId: string): Promise<void> {
  if (getGsc(brandId).importing) return;
  const siteUrl = getGsc(brandId).status?.siteUrl ?? '';
  dispatchGsc(brandId, { type: 'importStart' });
  let res;
  try {
    res = await searchConsoleApi.import(brandId);
  } catch (err) {
    const status = (err as { status?: number })?.status;
    dispatchGsc(brandId, { type: 'importFailed', error: status === 429 ? 'importLimited' : 'unavailable' });
    return;
  }
  dispatchGsc(brandId, { type: 'importOk', siteUrl, res });
  if (res.state === 'ready') await propose(brandId, firstSeeds(res.rows));
  else if (res.state === 'needs_property') await loadStatus(brandId); // the picker needs `candidates`
}

/**
 * One from-seeds call (C12). A 429 GEN_COOLDOWN waits `retryAfterSeconds` (≤ 10)
 * with the spinner kept and retries ONCE; a second 429 or any other error fails it.
 */
async function fromSeedsOnce(brandId: string, seeds: string[]) {
  try {
    return await brandQueriesApi.fromSeeds(brandId, seeds);
  } catch (err) {
    const body = (err as { body?: { code?: string; retryAfterSeconds?: number } })?.body;
    if (body?.code !== GEN_COOLDOWN_CODE) throw err;
    const wait = Math.min(10, Math.max(0, body.retryAfterSeconds ?? 10));
    await new Promise((r) => setTimeout(r, wait * 1000));
    return brandQueriesApi.fromSeeds(brandId, seeds);
  }
}

async function propose(brandId: string, seeds: string[]): Promise<void> {
  if (seeds.length === 0) return;
  dispatchGsc(brandId, { type: 'proposeStart', seeds });
  try {
    const res = await fromSeedsOnce(brandId, seeds);
    dispatchGsc(brandId, { type: 'proposeOk', seeds, proposals: res?.proposals ?? [] });
  } catch {
    dispatchGsc(brandId, { type: 'proposeFailed', seeds });
  }
  // Rows checked while the call ran (beyond the first 50) get theirs now.
  await propose(brandId, pendingSeeds(getGsc(brandId)));
}

/** « Réessayer » after « Les propositions n'ont pas pu être générées. »: re-runs only from-seeds. */
export async function retryProposals(brandId: string): Promise<void> {
  const s = getGsc(brandId);
  if (s.proposing.length) return;
  await propose(brandId, s.failedSeeds.slice(0, GSC_PROPOSE_MAX));
}

/** A row's checkbox. Checking a row with no proposal yet asks for one. */
export async function toggleRow(brandId: string, query: string): Promise<void> {
  dispatchGsc(brandId, { type: 'toggle', query });
  await propose(brandId, pendingSeeds(getGsc(brandId)));
}

/**
 * « Changer » / « Changer de propriété »: list the account's properties, then the picker.
 * `'none'` = the account has nothing else readable (the band sends the user to Google to
 * authorize another account); a failed call shows the unavailable line, the list stays.
 */
export async function openPicker(brandId: string): Promise<'picker' | 'none' | 'failed'> {
  if (getGsc(brandId).statusLoading) return 'failed';
  dispatchGsc(brandId, { type: 'statusStart' });
  try {
    dispatchGsc(brandId, { type: 'statusOk', status: await searchConsoleApi.status(brandId, { candidates: true }) });
  } catch {
    dispatchGsc(brandId, { type: 'statusFailed' });
    dispatchGsc(brandId, { type: 'chooseFailed' });
    return 'failed';
  }
  const st = getGsc(brandId).status;
  if (!st?.candidates?.length) return st?.state === 'connected' || st?.state === 'property_access_lost' ? 'none' : 'failed';
  dispatchGsc(brandId, { type: 'pick', on: true });
  return 'picker';
}

/**
 * « Déconnecter »: deletes our copy of the token and the brand's mapping (the privacy policy's
 * « la déconnexion dans Traaker supprime notre copie du jeton »; Google's grant is NOT revoked).
 */
export async function disconnectGsc(brandId: string): Promise<void> {
  if (getGsc(brandId).statusLoading) return;
  try {
    await searchConsoleApi.disconnect(brandId);
  } catch {
    dispatchGsc(brandId, { type: 'chooseFailed' });
    return;
  }
  await loadStatus(brandId);
}

/** A property picked in the chooser: PUT, then import it. */
export async function chooseProperty(brandId: string, siteUrl: string): Promise<void> {
  if (getGsc(brandId).choosing) return;
  dispatchGsc(brandId, { type: 'chooseStart', siteUrl });
  let status;
  try {
    status = await searchConsoleApi.setProperty(brandId, siteUrl);
  } catch {
    dispatchGsc(brandId, { type: 'chooseFailed' });
    return;
  }
  dispatchGsc(brandId, { type: 'chooseOk', status });
  if (status.state === 'connected') await runImport(brandId);
}

/** « Autoriser avec Google » / « Reconnecter » / another account: to Google's consent page. */
export async function authorize(
  brandId: string,
  returnPath: string,
  assign: (url: string) => void = (url) => window.location.assign(url),
): Promise<void> {
  if (getGsc(brandId).authorizing) return;
  dispatchGsc(brandId, { type: 'authorizeStart' });
  try {
    const { url } = await searchConsoleApi.authorize(brandId, returnPath);
    dispatchGsc(brandId, { type: 'authorizeEnd', failed: false });
    assign(url);
  } catch {
    dispatchGsc(brandId, { type: 'authorizeEnd', failed: true });
  }
}
